import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Snackbar, Typography } from '@mui/material'
import { Fund, Redemption, StrategyKey, UnitBatch } from '../api/types'
import { useOpsApi } from '../api/context'
import { apiError } from '../api/errors'
import { useCurrentUser } from '../auth'
import { navDeviation } from '../calc/preview'
import { InfoHint } from '../components/InfoHint'
import { PageHeader } from '../components/PageHeader'
import { QueueColumn, QueueTable } from '../components/QueueTable'
import { SegmentedTabs } from '../components/SegmentedTabs'
import { StatusChip } from '../components/StatusChip'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { BatchDetailDialog } from './jednotky/BatchDetailDialog'
import { formatDate, formatDateTime, formatUnits } from './jednotky/format'
import { BatchReviewPanel } from './kontrola/BatchReviewPanel'
import { ConfirmBatchDialog } from './kontrola/ConfirmBatchDialog'
import { downloadBatchReport } from './kontrola/report'
import { ConfirmRedemptionDialog } from './odkupy/ConfirmRedemptionDialog'
import { RedemptionDetailDialog } from './odkupy/RedemptionDetailDialog'
import { formatCzk, formatRate, formatUnits4 } from './odkupy/format'

const OPEN_STATUSES: UnitBatch['status'][] = ['DRAFT', 'CHECKED']

/** Sloupce tabulky přepočtů (vždy jednoho fondu, vybraného přepínačem). */
function batchColumns(): QueueColumn<UnitBatch>[] {
  return [
    {
      key: 'assignmentDate',
      label: 'Datum přidělení',
      getSortValue: (b) => b.assignmentDate,
      render: (b) => formatDate(b.assignmentDate),
    },
    {
      key: 'participants',
      label: 'Účastníků',
      align: 'right',
      getSortValue: (b) => b.participants.length,
      render: (b) => b.participants.length,
    },
    {
      key: 'units',
      label: 'Jednotky (před → po)',
      align: 'right',
      render: (b) => `${formatUnits(b.totalUnitsBefore)} → ${formatUnits(b.totalUnitsAfter)}`,
    },
    { key: 'status', label: 'Stav', render: (b) => <StatusChip status={b.status} /> },
    {
      key: 'createdAt',
      label: 'Vytvořil',
      getSortValue: (b) => b.createdAt,
      render: (b) => (
        <Box>
          <Typography variant="body2">{b.createdBy}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatDateTime(b.createdAt)}
          </Typography>
        </Box>
      ),
    },
  ]
}

/**
 * Fáze 5 — Kontrola + potvrzení, rozdělená na fondy stejným přepínačem jako Přidělení
 * jednotek. Nad otevřenými přepočty fondu (DRAFT/CHECKED): výběr → automatické testy
 * (invarianty) + AI kontrola + dashboard → POTVRDIT / ZAMÍTNOUT. Pod nimi oceněné
 * výběry fondu (REQUESTED), které potvrzuje CEO (zápis do core). Fázi vidí všichni
 * Apex uživatelé (backoffice náhled read-only), ale potvrzení a zamítnutí spouští jen
 * CEO (rozhodnutí F5-A; BE gating ve F7). Historii rozhodnutých přepočtů lze otevřít
 * jako audit detail.
 */
export function KontrolaPhase() {
  const api = useOpsApi()
  const user = useCurrentUser()

  const [funds, setFunds] = useState<Fund[]>([])
  const [fundKey, setFundKey] = useState<StrategyKey>('SEMI')
  // Přepočty i výběry se načítají napříč fondy (počty v přepínači), filtruje se na klientu.
  const [allOpenBatches, setOpenBatches] = useState<UnitBatch[]>([])
  const [allDecidedBatches, setDecidedBatches] = useState<UnitBatch[]>([])
  const [allRedemptions, setRedemptions] = useState<Redemption[]>([])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Chyba potvrzení/zamítnutí se drží zvlášť — patří DOVNITŘ dialogu, kde ji
  // obsluha uvidí (v těle stránky by ji překryl backdrop modálu).
  const [actionError, setActionError] = useState<string | null>(null)
  const [snackbar, setSnackbar] = useState<string | null>(null)

  const [selectedBatch, setSelectedBatch] = useState<UnitBatch | null>(null)
  const [navDeviationRatio, setNavDeviationRatio] = useState<number | null>(null)
  const [checking, setChecking] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [detailBatch, setDetailBatch] = useState<UnitBatch | null>(null)

  // Výběry k potvrzení (CEO).
  const [redemptionDetail, setRedemptionDetail] = useState<Redemption | null>(null)
  const [redemptionTarget, setRedemptionTarget] = useState<Redemption | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [f, all, red] = await Promise.all([
        api.listFunds(),
        api.listBatches(),
        api.listRedemptions({ status: 'REQUESTED' }),
      ])
      setFunds(f)
      setOpenBatches(all.filter((b) => OPEN_STATUSES.includes(b.status)))
      setDecidedBatches(all.filter((b) => !OPEN_STATUSES.includes(b.status)))
      setRedemptions(red)
      // Výchozí fond nemusí v tenantu existovat — spadni na první.
      setFundKey((key) => (f.length > 0 && !f.some((x) => x.key === key) ? f[0].key : key))
    } catch (err) {
      setError(apiError(err, 'Načtení přepočtů selhalo'))
    } finally {
      setLoading(false)
    }
  }, [api])

  useEffect(() => {
    reload()
  }, [reload])

  const openBatches = useMemo(
    () => allOpenBatches.filter((b) => b.fundKey === fundKey),
    [allOpenBatches, fundKey]
  )
  const decidedBatches = useMemo(
    () => allDecidedBatches.filter((b) => b.fundKey === fundKey),
    [allDecidedBatches, fundKey]
  )
  const redemptions = useMemo(
    () => allRedemptions.filter((r) => r.fundKey === fundKey),
    [allRedemptions, fundKey]
  )

  /** Kolik čeká na rozhodnutí ve fondu (přepočty + výběry), ukazuje se v přepínači. */
  const pendingCount = (key: string) =>
    allOpenBatches.filter((b) => b.fundKey === key).length +
    allRedemptions.filter((r) => r.fundKey === key).length

  const handleFundChange = (key: string) => {
    setFundKey(key as StrategyKey)
    // Vybraný přepočet patří jinému fondu.
    setSelectedBatch(null)
  }

  /**
   * Výběr přepočtu ke kontrole: dopočítá odchylku NAV proti minulému ocenění a —
   * pokud kontrola ještě neproběhla — spustí testy + AI. Už zkontrolovaný přepočet
   * se jen zobrazí; opakované spuštění je za tlačítkem „Spustit kontrolu znovu",
   * aby prosté proklikávání fronty nevolalo AI (placené) při každém kliku.
   */
  const handleSelect = useCallback(
    async (batch: UnitBatch, force = false) => {
      if (checking) return
      setSelectedBatch(batch)
      setChecking(true)
      setError(null)
      try {
        const valuations = await api.listValuations(batch.fundKey)
        const current = valuations.find((v) => v.id === batch.valuationId)
        // listValuations řadí sestupně dle data → první starší = minulé ocenění.
        const previous = current ? valuations.find((v) => v.date < current.date) : undefined
        setNavDeviationRatio(navDeviation(batch.navTotal, previous?.navTotal))

        const alreadyChecked = batch.status === 'CHECKED' && Boolean(batch.check)
        if (force || !alreadyChecked) {
          await api.runBatchChecks(batch.id)
          const checked = await api.aiReviewBatch(batch.id)
          setSelectedBatch(checked)
          setOpenBatches((prev) => prev.map((b) => (b.id === checked.id ? checked : b)))
        }
      } catch (err) {
        setError(apiError(err, 'Kontrola přepočtu selhala'))
      } finally {
        setChecking(false)
      }
    },
    [api, checking]
  )

  const handleRecheck = useCallback(() => {
    if (selectedBatch) handleSelect(selectedBatch, true)
  }, [handleSelect, selectedBatch])

  const selectedFund = useMemo(
    () => (selectedBatch ? funds.find((f) => f.key === selectedBatch.fundKey) : undefined),
    [funds, selectedBatch]
  )

  const handleConfirm = async (sendEmail: boolean) => {
    if (!selectedBatch) return
    setSubmitting(true)
    setActionError(null)
    try {
      await api.confirmBatch(selectedBatch.id, { sendEmail })
      setSnackbar(
        `Přepočet potvrzen — jednotky připsány${sendEmail ? ', klienti informováni emailem' : ''}`
      )
      setConfirmOpen(false)
      setSelectedBatch(null)
      await reload()
    } catch (err) {
      setActionError(apiError(err, 'Potvrzení přepočtu selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    if (!selectedBatch) return
    setSubmitting(true)
    setActionError(null)
    try {
      await api.rejectBatch(selectedBatch.id)
      setSnackbar('Přepočet zamítnut — vklady vráceny do fronty plateb')
      setRejectOpen(false)
      setSelectedBatch(null)
      await reload()
    } catch (err) {
      setActionError(apiError(err, 'Zamítnutí přepočtu selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownloadReport = () => {
    if (selectedBatch && selectedFund) {
      downloadBatchReport(selectedBatch, selectedFund, navDeviationRatio)
    }
  }

  const openRedemptionDetail = async (redemption: Redemption) => {
    setRedemptionDetail(redemption)
    // Kontroly a tranše raději čteme čerstvé.
    try {
      setRedemptionDetail(await api.getRedemption(redemption.id))
    } catch {
      // Zůstane detail z fronty.
    }
  }

  const handleConfirmRedemption = async (sendEmail: boolean) => {
    if (!redemptionTarget) return
    setSubmitting(true)
    try {
      await api.confirmRedemption(redemptionTarget.id, { sendEmail })
      setSnackbar('Výběr potvrzen. Jednotky jsou prodané, výplatu označte ve Vklady / výběry.')
      setRedemptionTarget(null)
      setRedemptionDetail(null)
      await reload()
    } catch (err) {
      setSnackbar(apiError(err, 'Potvrzení výběru selhalo'))
    } finally {
      setSubmitting(false)
    }
  }

  const redemptionColumns: QueueColumn<Redemption>[] = [
    { key: 'clientName', label: 'Klient', getSortValue: (r) => r.clientName, render: (r) => r.clientName },
    { key: 'feeClass', label: 'Třída', render: (r) => (r.feeClass === 'A' ? 'A (EB)' : 'B (NORM)') },
    {
      key: 'units',
      label: 'Jednotky',
      align: 'right',
      getSortValue: (r) => r.units,
      render: (r) => formatUnits4(r.units),
    },
    { key: 'unitPrice', label: 'Cena', align: 'right', render: (r) => formatUnits(r.unitPrice) },
    {
      key: 'grossAmount',
      label: 'Hrubá hodnota',
      align: 'right',
      getSortValue: (r) => r.grossAmount,
      render: (r) => formatCzk(r.grossAmount),
    },
    {
      key: 'sfFee',
      label: 'Odměna',
      align: 'right',
      getSortValue: (r) => r.sfFee,
      render: (r) => (
        <Box>
          <Typography variant="body2">{formatCzk(r.sfFee)}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatRate(r.sfRate)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'exitFee',
      label: 'Výst. poplatek',
      align: 'right',
      getSortValue: (r) => r.exitFee,
      render: (r) => (
        <Box>
          <Typography variant="body2">{formatCzk(r.exitFee)}</Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatRate(r.exitFeeRate)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'netAmount',
      label: 'Čistá výplata',
      align: 'right',
      getSortValue: (r) => r.netAmount,
      render: (r) => <strong>{formatCzk(r.netAmount)}</strong>,
    },
    {
      key: 'valuationDate',
      label: 'Ocenění',
      getSortValue: (r) => r.valuationDate ?? '',
      render: (r) => (r.valuationDate ? formatDate(r.valuationDate) : ''),
    },
    {
      key: 'actions',
      label: 'Akce',
      align: 'right',
      render: (r) => (
        <Box sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Button
            size="small"
            variant="text"
            onClick={(event) => {
              event.stopPropagation()
              openRedemptionDetail(r)
            }}
          >
            Detail
          </Button>
          {user.isCeo && (
            <Button
              size="small"
              variant="contained"
              disabled={submitting}
              onClick={(event) => {
                event.stopPropagation()
                setRedemptionTarget(r)
              }}
            >
              Potvrdit
            </Button>
          )}
        </Box>
      ),
    },
  ]

  const sigma = selectedBatch ? selectedBatch.totalUnitsAfter - selectedBatch.totalUnitsBefore : 0

  return (
    <Box sx={{ maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Kontrola přidělení"
        subtitle="Automatické testy + AI kontrola + dashboard nad přepočtem → POTVRDIT / ZAMÍTNOUT."
        info={
          <InfoHint
            title="Invarianty před potvrzením"
            lines={[
              'Σ jednotek účastníků = přírůstek celkových jednotek fondu.',
              'cena × jednotky ≈ přijatý vklad (tolerance zaokrouhlení).',
              'NAV fondu > 0 a datum přidělení ≥ potvrzení plateb.',
              'Žádný účastník dvakrát v otevřeném přepočtu.',
              'POTVRDIT = jednotky se připíší klientům (core Deal BUY per účastník, idempotentní) + plný přepočet portfolií; před zásahem se dělá záloha DB.',
              'Výběry k potvrzení jsou oceněné výběry fondu. Potvrzení CEO prodá jednotky, strhne odměnu a poplatek a sníží celkové jednotky fondu.',
              'Výplatu pak CEO označí ve Vklady / výběry.',
            ]}
          />
        }
      />

      <SegmentedTabs
        items={funds.map((f) => {
          const n = pendingCount(f.key)
          return { value: f.key, label: n > 0 ? `${f.key} (${n})` : f.key }
        })}
        value={fundKey}
        onChange={handleFundChange}
      />

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              K vyřízení ({openBatches.length})
            </Typography>
            <QueueTable
              columns={batchColumns()}
              rows={openBatches}
              getRowKey={(b) => b.id}
              defaultSortKey="createdAt"
              defaultSortDir="asc"
              emptyText="Žádné přepočty k vyřízení"
              onRowClick={handleSelect}
            />
          </Box>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Výběry k potvrzení ({redemptions.length})
            </Typography>
            <QueueTable
              columns={redemptionColumns}
              rows={redemptions}
              getRowKey={(r) => r.id}
              getSearchText={(r) => r.clientName}
              searchPlaceholder="Hledat klienta…"
              defaultSortKey="clientName"
              defaultSortDir="asc"
              emptyText="Žádné oceněné výběry k potvrzení"
              onRowClick={openRedemptionDetail}
            />
            {!user.isCeo && redemptions.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Výběry potvrzuje CEO.
              </Typography>
            )}
          </Box>

          {selectedBatch && selectedFund && (
            <BatchReviewPanel
              batch={selectedBatch}
              fund={selectedFund}
              navDeviationRatio={navDeviationRatio}
              isCeo={user.isCeo}
              checking={checking}
              submitting={submitting}
              onDownloadReport={handleDownloadReport}
              onRecheck={handleRecheck}
              onConfirmClick={() => {
                setActionError(null)
                setConfirmOpen(true)
              }}
              onRejectClick={() => {
                setActionError(null)
                setRejectOpen(true)
              }}
            />
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Historie rozhodnutých přepočtů (audit trail)
            </Typography>
            <QueueTable
              columns={batchColumns()}
              rows={decidedBatches}
              getRowKey={(b) => b.id}
              defaultSortKey="createdAt"
              defaultSortDir="desc"
              emptyText="Žádné rozhodnuté přepočty"
              onRowClick={setDetailBatch}
            />
          </Box>
        </>
      )}

      {selectedBatch && (
        <ConfirmBatchDialog
          open={confirmOpen}
          batchId={selectedBatch.id}
          fundName={selectedFund?.name ?? selectedBatch.fundKey}
          unitsAssigned={formatUnits(sigma)}
          participantCount={selectedBatch.participants.length}
          submitting={submitting}
          error={actionError}
          onConfirm={handleConfirm}
          onClose={() => setConfirmOpen(false)}
        />
      )}

      <ConfirmDialog
        open={rejectOpen}
        title="Zamítnout přepočet"
        text="Celý přepočet se uzavře, vklady se vrátí do fronty plateb (nic se nepřipíše)."
        confirmLabel="zamítnout"
        danger
        doubleConfirm
        submitting={submitting}
        error={actionError}
        onConfirm={handleReject}
        onClose={() => setRejectOpen(false)}
      />

      <RedemptionDetailDialog
        open={Boolean(redemptionDetail)}
        redemption={redemptionDetail}
        submitting={submitting}
        isCeo={user.isCeo}
        isBackoffice={user.isBackoffice}
        onConfirmClick={() => setRedemptionTarget(redemptionDetail)}
        onClose={() => setRedemptionDetail(null)}
      />

      <ConfirmRedemptionDialog
        open={Boolean(redemptionTarget)}
        redemption={redemptionTarget}
        submitting={submitting}
        onConfirm={handleConfirmRedemption}
        onClose={() => setRedemptionTarget(null)}
      />

      <BatchDetailDialog
        open={Boolean(detailBatch)}
        batch={detailBatch}
        onClose={() => setDetailBatch(null)}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbar ?? ''}
      />
    </Box>
  )
}
